import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Popconfirm, Space, Tooltip } from 'antd';
import {
  BarChartOutlined,
  EditOutlined,
  LinkOutlined,
} from '@ant-design/icons';
// API
import { UPDATE_CB_PROMO_CODE } from 'api/cbPromocodes/mutations';
// Types
import {
  UpdateCbPromoCode,
  UpdateCbPromoCodeVariables,
} from 'api/cbPromocodes/types/UpdateCbPromoCode';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Helpers
import {
  CbPromoCode,
  buildPromoCodeShareLink,
} from 'helpers/cbPromoCodes/helpers';
// Components
import EditCbPromoCodeModal from '../EditCbPromoCodeModal/EditCbPromoCodeModal';
import RedemptionsModal from '../RedemptionsModal/RedemptionsModal';

export type ActionsProps = {
  promoCode: CbPromoCode;
};

const Actions: React.FC<ActionsProps> = ({ promoCode }) => {
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [redemptionsVisible, setRedemptionsVisible] = useState<boolean>(false);

  const [updateCbPromoCode, { loading }] = useMutation<
    UpdateCbPromoCode,
    UpdateCbPromoCodeVariables
  >(UPDATE_CB_PROMO_CODE);

  const isActive = promoCode.isActive;

  const handleToggleActive = async (): Promise<void> => {
    try {
      await updateCbPromoCode({
        variables: { input: { id: promoCode.id, isActive: !isActive } },
      });
      successNotification(
        isActive ? 'Promo code paused.' : 'Promo code activated.'
      );
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(
        buildPromoCodeShareLink(promoCode.code)
      );
      successNotification('Share link copied to clipboard');
    } catch (error) {
      errorNotification('Could not copy link');
    }
  };

  return (
    <Space size="small">
      <Tooltip title="Copy share link">
        <Button size="small" icon={<LinkOutlined />} onClick={handleCopyLink} />
      </Tooltip>

      <Tooltip title="Redemptions & analytics">
        <Button
          size="small"
          icon={<BarChartOutlined />}
          onClick={() => setRedemptionsVisible(true)}
        />
      </Tooltip>

      <Tooltip title="Edit">
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => setEditVisible(true)}
        />
      </Tooltip>

      <Popconfirm
        title={
          isActive ? 'Pause this promo code?' : 'Activate this promo code?'
        }
        okText="Yes"
        cancelText="No"
        onConfirm={handleToggleActive}
      >
        <Button size="small" danger={isActive} loading={loading}>
          {isActive ? 'Pause' : 'Activate'}
        </Button>
      </Popconfirm>

      {editVisible && (
        <EditCbPromoCodeModal
          promoCode={promoCode}
          show={editVisible}
          onCancel={() => setEditVisible(false)}
        />
      )}

      {redemptionsVisible && (
        <RedemptionsModal
          promoCode={promoCode}
          show={redemptionsVisible}
          onCancel={() => setRedemptionsVisible(false)}
        />
      )}
    </Space>
  );
};

export default Actions;
